import React, { useEffect, useState } from "react";
import { ChevronDown, Info, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import { OtcRequestItem, OtcSettlementProvider } from "../types";
import { useOtcProviders } from "../hooks";
import { SettlementMethodDropdown } from "./SettlementMethodDropdown";

export interface ValidityCalculation {
  validForValue: number;
  validForUnit: string;
  validityInMinutes: number;
  expiringDate: string;
  expiringTime: string;
}

export const convertToMinutes = (
  value: number | string,
  unit: string
): number => {
  const num = Math.max(1, Number(value) || 1);
  const normalizedUnit = (unit || "Minutes").toLowerCase().trim();

  if (normalizedUnit.includes("hour")) {
    return num * 60;
  }
  if (normalizedUnit.includes("day")) {
    return num * 24 * 60;
  }
  return num;
};

export const calculateExpiry = (
  value: number | string,
  unit: string
): ValidityCalculation => {
  const num = Math.max(1, Number(value) || 1);
  const normalizedUnit = (unit || "Minutes").toLowerCase().trim();
  const validityInMinutes = convertToMinutes(num, normalizedUnit);

  const now = new Date();
  now.setMinutes(now.getMinutes() + validityInMinutes);

  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  const expiringDate = `${day}/${month}/${year}`;

  const rawHours = now.getHours();
  const period = rawHours >= 12 ? "PM" : "AM";
  const hours = rawHours % 12 || 12;
  const expiringTime = `${String(hours).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")} ${period}`;

  return {
    validForValue: num,
    validForUnit: unit || "Minutes",
    validityInMinutes,
    expiringDate,
    expiringTime,
  };
};

export const VALID_FOR_UNITS = ["Minutes", "Hours"] as const;

interface ApproveRequestModalProps {
  open: boolean;
  request: OtcRequestItem | null;
  onClose: () => void;
  onConfirm: (
    providerId: string,
    note?: string,
    validity?: ValidityCalculation
  ) => void;
  onSendCounterOffer?: () => void;
  isSubmitting?: boolean;
}

export const SETTLEMENT_OPTIONS = [
  { label: "External transfer", subtitle: "Use External", value: "external" },
  { label: "Nuvion", subtitle: "Use Nuvion", value: "Nuvion" },
];

export const ApproveRequestModal: React.FC<ApproveRequestModalProps> = ({
  open,
  request,
  onClose,
  onConfirm,
  onSendCounterOffer,
  isSubmitting = false,
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [validForValue, setValidForValue] = useState<number | string>(7);
  const [validForUnit, setValidForUnit] = useState<string>("");

  const currencyFrom = (request?.currency || "USD").toUpperCase();
  const currencyTo = (request?.currencyTo || "NGN").toUpperCase();

  const { providers } = useOtcProviders({
    currencyFrom,
    currencyTo,
    enabled: Boolean(open && request),
  });

  useEffect(() => {
    if (!open || !request) return;
    if (providers.length > 0) {
      setSelectedProviderId((prev) => {
        const exists = providers.some((prov: OtcSettlementProvider) => prov.id === prev);
        return exists ? prev : providers[0].id;
      });
    }
    setValidForValue(7);
    setValidForUnit("");
  }, [open, request, providers]);

  if (!open || !request) return null;

  const selectedProvider =
    providers.find((prov: OtcSettlementProvider) => prov.id === selectedProviderId) || providers[0];
  const activeProviderId = selectedProvider?.id || selectedProviderId;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validity = calculateExpiry(validForValue, validForUnit);
    onConfirm(activeProviderId, undefined, validity);
  };

  // Format currency numbers
  const formattedAmount = (request.amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Dynamic wallet balance check against transfer amount
  const customerBalanceNum =
    typeof request.customer?.walletBalance === "string"
      ? parseFloat(request.customer.walletBalance.replace(/[^0-9.]/g, "")) || 0
      : Number(request.customer?.walletBalance || 0);

  const transferAmount = Number(request.amount) || 0;
  const isInsufficientBalance = customerBalanceNum < transferAmount;

  // Validation: all required fields must be selected
  const isValidForValue = Boolean(validForValue && Number(validForValue) > 0);
  const isValidForUnit = Boolean(validForUnit && validForUnit.trim());
  const isSettlementMethodSelected = Boolean(activeProviderId && activeProviderId.trim());
  const isFormValid = isValidForValue && isValidForUnit && isSettlementMethodSelected;

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/60 backdrop-blur-sm"
      contentClassName="max-w-[490px] bg-white rounded-[20px] shadow-2xl border border-[#F2F4F7]"
    >
      <ModalHeader className="flex items-center justify-between px-6 pt-6 pb-2">
        <ModalTitle className="text-[20px] font-semibold text-[#101828]">
          Approve this request
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={handleClose}
          className="rounded-full p-1 text-[#667085] hover:bg-[#F2F4F7] hover:text-[#101828] transition-colors disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-4 px-6 py-4">
          {/* Top Summary Details Box */}
          <div className="rounded-[12px] bg-[#F8F9FC] p-4 space-y-2">
            <p className="text-[13px] text-[#475467]">
              Requested rate:{" "}
              <span className="font-medium text-[#101828]">
                {request.requestedRate ||
                  (request.requestedRateValue
                    ? `₦${request.requestedRateValue.toLocaleString()} / USD`
                    : "-")}
              </span>
            </p>
            <p className="text-[13px] text-[#475467]">
              Transfer amount:{" "}
              <span className="font-medium text-[#101828]">
                ${formattedAmount} {request.currency || "USD"}
              </span>
            </p>
            <p className="text-[13px] text-[#475467]">
              Recipient to receive:{" "}
              <span className="font-medium text-[#101828]">
                {request.recipientToReceive || "-"}
              </span>
            </p>
          </div>

          {/* Valid for row matching new design */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-[#344054]">
              Valid for
            </span>
            <div className="grid grid-cols-2 gap-3">
              {/* Number Input */}
              <div className="flex h-[48px] w-full items-center rounded-[8px] bg-[#F2F4F7] px-3.5 focus-within:ring-1 focus-within:ring-[#D0D5DD]">
                <input
                  type="number"
                  min={1}
                  value={validForValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    setValidForValue(val === "" ? "" : Math.max(1, parseInt(val) || 1));
                  }}
                  placeholder="7"
                  className="w-full bg-transparent text-[14px] font-medium text-[#101828] outline-none"
                />
              </div>

              {/* Units Dropdown */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#F2F4F7] px-3.5 text-[14px] font-medium text-[#101828] outline-none hover:bg-[#EAECF0]"
                  >
                    <span className={validForUnit ? "text-[#101828]" : "text-[#667085]"}>
                      {validForUnit || "Minutes/Hours"}
                    </span>
                    <ChevronDown size={16} className="text-[#667085] shrink-0" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="start"
                    sideOffset={4}
                    className="z-[150] w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-[8px] border border-[#E4E7EC] bg-white p-1 shadow-md"
                  >
                    {VALID_FOR_UNITS.map((unit) => (
                      <DropdownMenu.Item
                        key={unit}
                        onSelect={() => setValidForUnit(unit)}
                        className={`flex items-center cursor-pointer rounded-[6px] px-3 py-2 text-[13px] outline-none transition-colors ${
                          validForUnit === unit
                            ? "bg-[#F4F5FA] font-medium text-[#101828]"
                            : "text-[#344054] hover:bg-[#F9FAFB]"
                        }`}
                      >
                        <span>{unit}</span>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </div>

          {/* Settlement Method */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-[#344054]">
              Settlement method
            </span>
            <SettlementMethodDropdown
              selectedProviderId={selectedProviderId}
              onSelectProvider={setSelectedProviderId}
              currencyFrom={currencyFrom}
              currencyTo={currencyTo}
              disabled={isSubmitting}
            />
          </div>

          {/* Dynamic Insufficient balance notice banner */}
          {isInsufficientBalance && (
            <div className="flex items-center gap-2.5 rounded-[8px] bg-[#EEF4FF] p-3 text-[13px]">
              <Info size={16} className="text-[#2E90FA] shrink-0" />
              <span className="font-normal text-[#3538CD]">
                The customer’s wallet {request.customer?.walletBalance ? `(${request.customer.walletBalance}) ` : ""}is not sufficiently funded.
              </span>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                handleClose();
                onSendCounterOffer?.();
              }}
              className="text-[13px] font-semibold text-[#101828] transition-colors hover:text-[#6F00FF] disabled:opacity-50"
            >
              Send counter offer
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="h-10 rounded-[8px] px-4 py-2 text-[13px] font-medium text-[#344054] transition-colors hover:text-[#101828] disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={isSubmitting || !isFormValid}
              >
                {isSubmitting ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        </ModalBody>
      </form>
    </Modal>
  );
};
